import React from "react";

import { mdiCloudDownloadOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import CalendarExportAdBreakScteForm from "./CalendarExportAdBreakScteForm";
import CalendarExportAsRunForm from "./CalendarExportAsRunForm";
import FastAPI from "../../../api/FastAPI";
import {
  EXPORT_SCHEDULING_REPORT_FORM_ID,
  EXPORT_SCHEDULING_REPORT_MODAL_ID,
} from "../../../constants/fast";
import { BUTTON_VARIANTS } from "../../button/Button";
import Modal from "../../modal/Modal";
import MultiToggle from "../../multiToggle/MultiToggle";

import type { JSX } from "react";

import type { ModalActions } from "../../modal/Modal";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

function ModalHeader(): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  return (
    <div className="flex flex-wrap items-center gap-[0.9375rem]">
      <div className="border-border flex rounded-[0.46875rem] border p-[0.46875rem]">
        <Icon path={mdiCloudDownloadOutline} size={1.25} />
      </div>
      <div className="text-[1.171875rem] font-semibold">
        {t("exportSchedulingReports")}
      </div>
    </div>
  );
}

interface CalendarExportAsRunModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CalendarExportModal({
  isOpen,
  onClose,
}: CalendarExportAsRunModalProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const { channelId } = useParams<FASTChannelRoutesParams>();

  const [toggleValue, setToggleValue] = React.useState<string>("asRun");

  const labelButton = (
    <div className="flex items-center justify-center gap-[0.46875rem]">
      <Icon path={mdiCloudDownloadOutline} size={0.9375} />
      {t("downloadReport")}
    </div>
  );

  const getScheduleListQuery = FastAPI.generateQuery({
    fn: FastAPI.getSchedules,
    params: [{ channelId }],
  });

  const { data: scheduleList, isLoading: isLoadingScheduleList } =
    useQuery(getScheduleListQuery);

  const confirmModalActions: ModalActions[] = [
    {
      label: labelButton,
      buttonVariant: BUTTON_VARIANTS.PRIMARY,
      isFullWidth: true,
      isCloseAfterAction: false,
      formId: EXPORT_SCHEDULING_REPORT_FORM_ID,
      isDisabled: isLoadingScheduleList,
    },
  ];

  const options = [
    { label: t("asRun"), value: "asRun" },
    { label: t("adBreakScte"), value: "adBreakScte" },
  ];

  const isAsRun = toggleValue === "asRun";
  const isAdBreakScte = toggleValue === "adBreakScte";

  return (
    <Modal
      isDivided
      actions={confirmModalActions}
      className="min-h-[560px]"
      header={<ModalHeader />}
      id={EXPORT_SCHEDULING_REPORT_MODAL_ID}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <div className="flex flex-col gap-[0.9375rem]">
        <MultiToggle
          isFullWidth
          onChange={setToggleValue}
          options={options}
          value={toggleValue}
        />
        {isAsRun ? (
          <CalendarExportAsRunForm scheduleList={scheduleList} />
        ) : null}
        {isAdBreakScte ? (
          <CalendarExportAdBreakScteForm scheduleList={scheduleList} />
        ) : null}
      </div>
    </Modal>
  );
}

export default CalendarExportModal;
